import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Typography,
  TextField,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Pagination,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Search, Save } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import Header from "../components/Header";

const PAGE_SIZE = 25;
const API = process.env.REACT_APP_BACKEND_URL;

const TranslationsPage = () => {
  const [namespace, setNamespace] = useState("portal");
  const [rows, setRows] = useState([]); // merged: { tkey, enId, enValue, arId, arValue }
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [edits, setEdits] = useState({}); // { id: newValue }
  const [saving, setSaving] = useState({}); // { id: bool }

  const fetchTranslations = useCallback(async () => {
    setLoading(true);
    setEdits({});
    try {
      const res = await axios.get(
        `${API}/api/translations?namespace=${namespace}&limit=2000`,
        { withCredentials: true }
      );
      // Merge EN + AR rows by tkey into single display rows
      const map = {};
      for (const row of res.data.data) {
        if (!map[row.tkey]) map[row.tkey] = { tkey: row.tkey };
        if (row.locale === "en") {
          map[row.tkey].enId = row.id;
          map[row.tkey].enValue = row.value;
        } else if (row.locale === "ar") {
          map[row.tkey].arId = row.id;
          map[row.tkey].arValue = row.value;
        }
      }
      setRows(
        Object.values(map).sort((a, b) => a.tkey.localeCompare(b.tkey))
      );
    } catch {
      toast.error("Failed to load translations");
    } finally {
      setLoading(false);
    }
  }, [namespace]);

  useEffect(() => {
    fetchTranslations();
    setPage(1);
    setSearch("");
  }, [fetchTranslations]);

  const handleEdit = (id, value) => {
    setEdits((prev) => ({ ...prev, [id]: value }));
  };

  const handleSave = async (id, originalValue) => {
    const newValue = edits[id];
    if (newValue === undefined || newValue === originalValue) return;
    setSaving((prev) => ({ ...prev, [id]: true }));
    try {
      await axios.put(
        `${API}/api/translations/${id}`,
        { value: newValue },
        { withCredentials: true }
      );
      setRows((prev) =>
        prev.map((row) => {
          if (row.enId === id) return { ...row, enValue: newValue };
          if (row.arId === id) return { ...row, arValue: newValue };
          return row;
        })
      );
      setEdits((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.success("Saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleSaveRow = async (row) => {
    const enDirty =
      row.enId &&
      edits[row.enId] !== undefined &&
      edits[row.enId] !== row.enValue;
    const arDirty =
      row.arId &&
      edits[row.arId] !== undefined &&
      edits[row.arId] !== row.arValue;
    if (enDirty) await handleSave(row.enId, row.enValue);
    if (arDirty) await handleSave(row.arId, row.arValue);
  };

  const filteredRows = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      r.tkey.toLowerCase().includes(s) ||
      (r.enValue || "").toLowerCase().includes(s) ||
      (r.arValue || "").includes(s)
    );
  });

  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE);
  const pageRows = filteredRows.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  return (
    <Box className="main-container mt-4">
      <Header
        title="Translations"
        subtitle="Edit portal and mobile app text — changes apply instantly without redeploying"
      />

      <Paper sx={{ mt: 3, p: 3, borderRadius: 3, boxShadow: 2 }}>
        {/* Filter bar */}
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          mb={2}
          flexWrap="wrap"
        >
          <ToggleButtonGroup
            value={namespace}
            exclusive
            onChange={(_, v) => v && setNamespace(v)}
            size="small"
          >
            <ToggleButton value="portal">Portal</ToggleButton>
            <ToggleButton value="mobile">Mobile</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            size="small"
            placeholder="Search by key or value…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{ flexGrow: 1, maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
            {filteredRows.length} keys
          </Typography>
        </Box>

        {/* Table */}
        <TableContainer
          sx={{
            maxHeight: "65vh",
            overflow: "auto",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { height: 8, width: 8 },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f0f0f0",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#c1c1c1",
              borderRadius: 8,
              border: "2px solid #f0f0f0",
            },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "24%", fontWeight: 700 }}>
                  Key
                </TableCell>
                <TableCell sx={{ width: "35%", fontWeight: 700 }}>
                  English (EN)
                </TableCell>
                <TableCell sx={{ width: "35%", fontWeight: 700 }}>
                  Arabic (AR)
                </TableCell>
                <TableCell
                  sx={{ width: "6%", fontWeight: 700 }}
                  align="center"
                >
                  Save
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      No translations found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((row) => {
                  const enDirty =
                    row.enId &&
                    edits[row.enId] !== undefined &&
                    edits[row.enId] !== row.enValue;
                  const arDirty =
                    row.arId &&
                    edits[row.arId] !== undefined &&
                    edits[row.arId] !== row.arValue;
                  const isDirty = enDirty || arDirty;
                  const isSavingRow =
                    saving[row.enId] || saving[row.arId];

                  return (
                    <TableRow
                      key={row.tkey}
                      hover
                      sx={
                        isDirty
                          ? { bgcolor: "action.selected" }
                          : undefined
                      }
                    >
                      {/* Key */}
                      <TableCell>
                        <Typography
                          variant="caption"
                          fontFamily="monospace"
                          color="text.secondary"
                          sx={{ wordBreak: "break-all" }}
                        >
                          {row.tkey}
                        </Typography>
                      </TableCell>

                      {/* EN */}
                      <TableCell>
                        {row.enId ? (
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            maxRows={3}
                            value={
                              edits[row.enId] !== undefined
                                ? edits[row.enId]
                                : row.enValue
                            }
                            onChange={(e) =>
                              handleEdit(row.enId, e.target.value)
                            }
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                fontSize: "0.82rem",
                              },
                            }}
                          />
                        ) : (
                          <Typography
                            color="text.disabled"
                            variant="caption"
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>

                      {/* AR */}
                      <TableCell>
                        {row.arId ? (
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            maxRows={3}
                            value={
                              edits[row.arId] !== undefined
                                ? edits[row.arId]
                                : row.arValue
                            }
                            onChange={(e) =>
                              handleEdit(row.arId, e.target.value)
                            }
                            variant="outlined"
                            inputProps={{
                              dir: "rtl",
                              style: { textAlign: "right" },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                fontSize: "0.82rem",
                              },
                            }}
                          />
                        ) : (
                          <Typography
                            color="text.disabled"
                            variant="caption"
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>

                      {/* Save */}
                      <TableCell align="center">
                        <Tooltip
                          title={isDirty ? "Save changes" : "No changes"}
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={!isDirty || isSavingRow}
                              onClick={() => handleSaveRow(row)}
                            >
                              {isSavingRow ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Save fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box mt={3} display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
              siblingCount={1}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TranslationsPage;
